import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowDownward as SortDescIcon,
  ArrowUpward as SortAscIcon,
  Clear as ClearSortIcon,
  PushPin as FreezeIcon,
  Search as SearchIcon,
  VisibilityOff as HideIcon,
} from "@mui/icons-material";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { STRONG_DROPDOWN_PAPER_SX } from "../../common/dropdownPaper";
import { ColumnTypeMap } from "./cellValue";
import { useDebouncedValue } from "../useDebouncedValue";
import {
  buildView,
  distinctValuesForColumn,
  findColumn,
} from "./sheetPipeline";
import { formatSummaryNumber, summarizeColumn } from "./selectionSummary";
import { ColumnId, CompiledView, SheetSource } from "./sheetTypes";
import {
  ColumnCondition,
  ColumnFilter,
  FilterOperator,
  SheetViewState,
  SortMode,
  SortRule,
} from "./sheetViewState";

/**
 * Everything one column can do, in a single popover: sort it, hide it, filter
 * it by a condition, or filter it by picking values. No nested submenus - they
 * are unusable at phone width, and this is the same panel on both.
 *
 * The checklist and the condition are staged and applied together, because a
 * checkbox that recomputes 50k rows on every click feels broken; the footer
 * shows what the pending filter would leave.
 */

const VALUE_ROW_HEIGHT = 32;
const VALUE_LIST_HEIGHT = 240;
const MAX_DISTINCT_VALUES = 1000;
const DRAFT_DEBOUNCE_MS = 250;

const TEXT_OPERATORS: Array<{ value: FilterOperator; label: string }> = [
  { value: "none", label: "No condition" },
  { value: "contains", label: "Contains" },
  { value: "notContains", label: "Does not contain" },
  { value: "equals", label: "Equals" },
  { value: "notEquals", label: "Does not equal" },
  { value: "beginsWith", label: "Begins with" },
  { value: "endsWith", label: "Ends with" },
  { value: "isBlank", label: "Is blank" },
  { value: "isNotBlank", label: "Is not blank" },
];

const NUMBER_OPERATORS: Array<{ value: FilterOperator; label: string }> = [
  { value: "none", label: "No condition" },
  { value: "equals", label: "Equals" },
  { value: "notEquals", label: "Does not equal" },
  { value: "gt", label: "Greater than" },
  { value: "gte", label: "Greater or equal" },
  { value: "lt", label: "Less than" },
  { value: "lte", label: "Less or equal" },
  { value: "between", label: "Between" },
  { value: "contains", label: "Contains" },
  { value: "isBlank", label: "Is blank" },
  { value: "isNotBlank", label: "Is not blank" },
];

const TYPE_LABEL: Record<string, string> = {
  text: "text",
  number: "number",
  date: "date",
  mixed: "mixed",
  empty: "empty",
};

interface ColumnMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  container?: HTMLElement | null;
  zIndex?: number;
  columnId: ColumnId | null;
  source: SheetSource;
  view: CompiledView;
  state: SheetViewState;
  types: ColumnTypeMap;
  emptyColumnCount: number;
  onClose: () => void;
  onApplyFilter: (columnId: ColumnId, filter: ColumnFilter | null) => void;
  onSort: (columnId: ColumnId, direction: "asc" | "desc" | null) => void;
  onSortMode: (columnId: ColumnId, mode: SortMode) => void;
  onHideColumn: (columnId: ColumnId) => void;
  onHideEmptyColumns: () => void;
  onFreezeThrough: (columnId: ColumnId) => void;
  onUnfreeze: () => void;
  isFrozen: boolean;
}

const ColumnMenu: React.FC<ColumnMenuProps> = ({
  open,
  anchorEl,
  container,
  zIndex,
  columnId,
  source,
  view,
  state,
  types,
  emptyColumnCount,
  onClose,
  onApplyFilter,
  onSort,
  onSortMode,
  onHideColumn,
  onHideEmptyColumns,
  onFreezeThrough,
  onUnfreeze,
  isFrozen,
}) => {
  const column = columnId ? findColumn(source, columnId) : null;
  const existing = columnId ? state.filters[columnId] : undefined;
  const columnType = columnId ? (types[columnId] ?? "text") : "text";
  const numeric = columnType === "number" || columnType === "date";

  // The draft is seeded once, on mount: SheetView gives this component a key per
  // column, so opening another column mounts a fresh draft instead of needing an
  // effect that would have to lie about its dependencies.
  const [valueSearch, setValueSearch] = useState("");
  const [checked, setChecked] = useState<Set<string> | null>(() =>
    existing?.values ? new Set(existing.values) : null,
  );
  const [condition, setCondition] = useState<ColumnCondition>(() =>
    existing?.condition
      ? { value2: "", ...existing.condition }
      : { operator: "none", value: "", value2: "" },
  );

  const debouncedSearch = useDebouncedValue(valueSearch, DRAFT_DEBOUNCE_MS);

  const distinct = useMemo(() => {
    if (!open || !columnId) {
      return {
        values: [],
        truncated: false,
        totalDistinct: 0,
        matchedDistinct: 0,
        blankCount: 0,
      };
    }
    return distinctValuesForColumn(
      source,
      state,
      columnId,
      types,
      MAX_DISTINCT_VALUES,
      debouncedSearch,
    );
  }, [open, columnId, source, state, types, debouncedSearch]);

  const columnSummary = useMemo(
    () => (open && columnId ? summarizeColumn(view, columnId) : null),
    [open, columnId, view],
  );

  /**
   * Figures for the column as it is on screen. The wording says "in view"
   * because these are the filtered rows, not the whole file - a total that
   * quietly means something else is worse than no total.
   */
  const summaryLine = useMemo(() => {
    if (!columnSummary) return "";
    const filled = `${columnSummary.filled.toLocaleString()} filled in view`;
    if (columnSummary.numeric > 0 && columnSummary.sum !== null) {
      return `${filled} - Sum ${formatSummaryNumber(columnSummary.sum)} - Average ${formatSummaryNumber(columnSummary.average ?? 0)} - Min ${formatSummaryNumber(columnSummary.min ?? 0)} - Max ${formatSummaryNumber(columnSummary.max ?? 0)}`;
    }
    return `${filled} - ${distinct.totalDistinct.toLocaleString()} distinct`;
  }, [columnSummary, distinct.totalDistinct]);

  const draftFilter = useMemo<ColumnFilter | null>(() => {
    if (!columnId) return null;
    const values = checked ? Array.from(checked) : null;
    const hasCondition = condition.operator !== "none";
    if (values === null && !hasCondition) return null;
    return {
      columnId,
      values,
      condition: hasCondition ? condition : null,
    };
  }, [columnId, checked, condition]);

  const debouncedDraft = useDebouncedValue(draftFilter, DRAFT_DEBOUNCE_MS);

  const pendingRows = useMemo(() => {
    if (!open || !columnId) return null;
    const filters = { ...state.filters };
    if (debouncedDraft) {
      filters[columnId] = debouncedDraft;
    } else {
      delete filters[columnId];
    }
    return buildView(source, { ...state, filters }, types).stats.visibleRows;
  }, [open, columnId, debouncedDraft, source, state, types]);

  const sortRule: SortRule | null = columnId
    ? (state.sort.find((rule) => rule.columnId === columnId) ?? null)
    : null;

  const toggleValue = useCallback(
    (value: string) => {
      setChecked((current) => {
        const base =
          current ?? new Set(distinct.values.map((entry) => entry.value));
        const next = new Set(base);
        if (next.has(value)) {
          next.delete(value);
        } else {
          next.add(value);
        }
        return next;
      });
    },
    [distinct.values],
  );

  const selectAllShown = useCallback(() => {
    setChecked((current) => {
      const next = new Set(current ?? []);
      distinct.values.forEach((entry) => next.add(entry.value));
      return next;
    });
  }, [distinct.values]);

  const clearAll = useCallback(() => setChecked(new Set<string>()), []);

  const apply = useCallback(() => {
    if (!columnId) return;
    onApplyFilter(columnId, draftFilter);
    onClose();
  }, [columnId, draftFilter, onApplyFilter, onClose]);

  const clearFilter = useCallback(() => {
    if (!columnId) return;
    onApplyFilter(columnId, null);
    onClose();
  }, [columnId, onApplyFilter, onClose]);

  const isChecked = useCallback(
    (value: string) => (checked ? checked.has(value) : true),
    [checked],
  );

  const operators = numeric ? NUMBER_OPERATORS : TEXT_OPERATORS;
  const needsValue =
    condition.operator !== "none" &&
    condition.operator !== "isBlank" &&
    condition.operator !== "isNotBlank";

  const ValueRow: React.FC<ListChildComponentProps> = ({ index, style }) => {
    const entry = distinct.values[index];
    const label = entry.value === "" ? "(Blanks)" : entry.value;
    return (
      <Box
        style={style}
        sx={{ display: "flex", alignItems: "center", pr: 1, minWidth: 0 }}
      >
        <FormControlLabel
          sx={{ flex: 1, minWidth: 0, mr: 0 }}
          control={
            <Checkbox
              size="small"
              checked={isChecked(entry.value)}
              onChange={() => toggleValue(entry.value)}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={label}
            >
              {label}
            </Typography>
          }
        />
        <Typography variant="caption" color="text.secondary">
          {entry.count.toLocaleString()}
        </Typography>
      </Box>
    );
  };

  return (
    <Popover
      open={open && !!column}
      anchorEl={anchorEl}
      onClose={onClose}
      container={container ?? undefined}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      slotProps={{
        paper: {
          elevation: 0,
          sx: [
            STRONG_DROPDOWN_PAPER_SX,
            { width: { xs: "calc(100vw - 32px)", sm: 360 }, maxWidth: "100%" },
          ],
        },
      }}
      sx={zIndex != null ? { zIndex } : undefined}
    >
      {column && (
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Box
            sx={{
              px: 1.5,
              pt: 1.5,
              pb: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={column.label || column.letter}
            >
              {column.letter}
              {column.label ? ` - ${column.label}` : ""}
            </Typography>
            <Chip
              size="small"
              label={TYPE_LABEL[columnType] ?? columnType}
              variant="outlined"
            />
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 1.5, pb: 1, display: "block" }}
          >
            {summaryLine}
          </Typography>

          <Box
            sx={{ px: 1, pb: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}
          >
            <Tooltip title="Sort A to Z (smallest first)">
              <IconButton
                size="small"
                color={sortRule?.direction === "asc" ? "primary" : "default"}
                onClick={() => onSort(column.id, "asc")}
                aria-label="Sort ascending"
              >
                <SortAscIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sort Z to A (largest first)">
              <IconButton
                size="small"
                color={sortRule?.direction === "desc" ? "primary" : "default"}
                onClick={() => onSort(column.id, "desc")}
                aria-label="Sort descending"
              >
                <SortDescIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove this column from the sort">
              <span>
                <IconButton
                  size="small"
                  disabled={!sortRule}
                  onClick={() => onSort(column.id, null)}
                  aria-label="Clear sort"
                >
                  <ClearSortIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                isFrozen
                  ? "Unfreeze columns"
                  : "Keep this column and the ones before it in place while the rest scrolls"
              }
            >
              <IconButton
                size="small"
                color={isFrozen ? "primary" : "default"}
                onClick={() => {
                  if (isFrozen) {
                    onUnfreeze();
                  } else {
                    onFreezeThrough(column.id);
                  }
                  onClose();
                }}
                aria-label={
                  isFrozen ? "Unfreeze columns" : "Freeze through this column"
                }
              >
                <FreezeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Hide this column">
              <IconButton
                size="small"
                onClick={() => {
                  onHideColumn(column.id);
                  onClose();
                }}
                aria-label="Hide column"
              >
                <HideIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {emptyColumnCount > 0 && (
              <Button
                size="small"
                onClick={() => {
                  onHideEmptyColumns();
                  onClose();
                }}
                sx={{ textTransform: "none" }}
              >
                Hide empty ({emptyColumnCount})
              </Button>
            )}
          </Box>

          {sortRule && (
            <Box sx={{ px: 1.5, pb: 1 }}>
              <Select
                size="small"
                fullWidth
                value={sortRule.mode}
                onChange={(event) =>
                  onSortMode(column.id, event.target.value as SortMode)
                }
                sx={{ fontSize: 13 }}
                MenuProps={{
                  container: container ?? undefined,
                  sx: zIndex != null ? { zIndex: zIndex + 1 } : undefined,
                }}
              >
                <MenuItem value="auto">
                  Sort as: auto ({TYPE_LABEL[columnType]})
                </MenuItem>
                <MenuItem value="text">Sort as: text</MenuItem>
                <MenuItem value="number">Sort as: number</MenuItem>
                <MenuItem value="date">Sort as: date</MenuItem>
              </Select>
            </Box>
          )}

          <Divider />

          <Box
            sx={{ px: 1.5, py: 1, display: "flex", gap: 1, flexWrap: "wrap" }}
          >
            <Select
              size="small"
              value={condition.operator}
              onChange={(event) =>
                setCondition((current) => ({
                  ...current,
                  operator: event.target.value as FilterOperator,
                }))
              }
              sx={{ flex: "1 1 140px", fontSize: 13 }}
              MenuProps={{
                container: container ?? undefined,
                sx: zIndex != null ? { zIndex: zIndex + 1 } : undefined,
              }}
            >
              {operators.map((operator) => (
                <MenuItem key={operator.value} value={operator.value}>
                  {operator.label}
                </MenuItem>
              ))}
            </Select>
            {needsValue && (
              <TextField
                size="small"
                value={condition.value}
                onChange={(event) =>
                  setCondition((current) => ({
                    ...current,
                    value: event.target.value,
                  }))
                }
                placeholder="Value"
                sx={{ flex: "1 1 120px" }}
              />
            )}
            {condition.operator === "between" && (
              <TextField
                size="small"
                value={condition.value2 ?? ""}
                onChange={(event) =>
                  setCondition((current) => ({
                    ...current,
                    value2: event.target.value,
                  }))
                }
                placeholder="and"
                sx={{ flex: "1 1 120px" }}
              />
            )}
          </Box>

          <Divider />

          <Box sx={{ px: 1.5, pt: 1 }}>
            <TextField
              size="small"
              fullWidth
              value={valueSearch}
              onChange={(event) => setValueSearch(event.target.value)}
              placeholder="Search values"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button
              size="small"
              onClick={selectAllShown}
              sx={{ textTransform: "none" }}
            >
              Select all ({distinct.values.length.toLocaleString()} shown)
            </Button>
            <Button
              size="small"
              onClick={clearAll}
              sx={{ textTransform: "none" }}
            >
              Clear
            </Button>
          </Box>

          {Object.keys(state.filters).some((id) => id !== column.id) && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 1.5 }}
            >
              Values from rows matching your other filters
            </Typography>
          )}

          <Box sx={{ px: 0.5 }}>
            {distinct.values.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 1.5, py: 2 }}
              >
                No values match your search.
              </Typography>
            ) : (
              <FixedSizeList
                height={Math.min(
                  VALUE_LIST_HEIGHT,
                  distinct.values.length * VALUE_ROW_HEIGHT,
                )}
                width="100%"
                itemCount={distinct.values.length}
                itemSize={VALUE_ROW_HEIGHT}
              >
                {ValueRow}
              </FixedSizeList>
            )}
          </Box>

          {distinct.truncated && (
            <Alert severity="warning" sx={{ borderRadius: 0, py: 0 }}>
              {distinct.values.length.toLocaleString()} of{" "}
              {distinct.totalDistinct.toLocaleString()} unique values shown.
              Type in the box above to search all values.
            </Alert>
          )}

          <Divider />

          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 1 }}
            >
              {pendingRows == null
                ? ""
                : `${pendingRows.toLocaleString()} rows match`}
            </Typography>
            <Button
              size="small"
              onClick={clearFilter}
              sx={{ textTransform: "none" }}
            >
              Clear filter
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={apply}
              sx={{ textTransform: "none" }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      )}
    </Popover>
  );
};

export default ColumnMenu;
